import { CheckCircle2, XCircle, Info, X } from 'lucide-react'
import { useToasts, dismissToast, type ToastVariant } from '@/lib/toast'

const VARIANT_STYLES: Record<ToastVariant, { rail: string; icon: typeof Info; iconColor: string }> = {
  success: { rail: 'bg-[#00c853]', icon: CheckCircle2, iconColor: 'text-[#80ffb4]' },
  error: { rail: 'bg-cmd-error', icon: XCircle, iconColor: 'text-cmd-on-error' },
  info: { rail: 'bg-primary', icon: Info, iconColor: 'text-primary' },
}

/**
 * Fixed-position toast stack for the command centre. Mounted once in
 * DashboardLayout; reads the module-level toast store. Confirms agent
 * decisions (approve / reject) and surfaces mutation failures.
 */
export function Toaster() {
  const toasts = useToasts()

  if (toasts.length === 0) return null

  return (
    <div
      className="fixed bottom-4 right-4 z-[100] flex w-[min(360px,calc(100vw-2rem))] flex-col gap-2"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
    >
      {toasts.map((t) => {
        const style = VARIANT_STYLES[t.variant]
        const Icon = style.icon
        return (
          <div
            key={t.id}
            className="relative flex items-start gap-3 bg-cmd-container p-3 pl-4 shadow-lg animate-in slide-in-from-right-4 fade-in duration-200"
          >
            <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${style.rail}`} />
            <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.iconColor}`} aria-hidden="true" />
            <p className="flex-1 text-sm text-white/90">{t.message}</p>
            <button
              onClick={() => dismissToast(t.id)}
              className="shrink-0 text-cmd-outline hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              aria-label="Dismiss notification"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )
      })}
    </div>
  )
}
